import { useState } from "react";
import { a3Copy } from "@/components/assignments/a3-copy";
import type { WritingTaskController } from "@/components/assignments/NativeWriting";
import { useSubmissionReceipt, checkSubmissionCommit } from "@/components/assignments/use-submission-receipt";
import type { Result } from "@/components/ui/box.schema";
import type { AssignmentDetail, Submission } from "@/lib/api/assignments";
import { when } from "./hand-in-model";

/** A cold reload finds the original server receipt even after the window closes. */
export function ReceivedHandIn({ received, timezone, renderTask, onSubmitted, onRefused }: {
  received: NonNullable<AssignmentDetail["received_hand_in"]>;
  timezone: string;
  renderTask: (task: WritingTaskController) => React.ReactNode;
  onSubmitted: (receipt: Submission) => void;
  onRefused: () => void;
}) {
  const [result, setResult] = useState<Result | null>(null);
  const keep = (next: Result) => { setResult(next); return next; };
  const receipt = useSubmissionReceipt({ onCommitted: () => undefined, onResult: keep,
    onReceipt: next => {
      onSubmitted(next);
      return keep({ state: "confirmed", at: next.submitted_at,
        effects: [a3Copy.handedInAttempt(next.attempt_number)], remains: [a3Copy.yourOriginalSubmissionTimeIsPreserved] });
    } });
  const waiting = (): Result => ({ state: "unknown", requestId: received.key,
    cap: a3Copy.receivedVerificationPending, sentence: a3Copy.receivedWorkKept,
    check: { verb: a3Copy.checkHandIn, note: a3Copy.thisReadsTheSameHandInItNeverSends,
      run: async () => {
        try {
          const checked = await checkSubmissionCommit<{ submission_id: string }>(received.key);
          if (checked.state === "confirmed") return receipt.confirm(checked.receipt);
          if (checked.state === "not_committed") {
            onRefused();
            return keep({ state: "refused", cap: a3Copy.notHandedIn,
              sentence: checked.reason ?? a3Copy.noHandInWasRecordedForThisRequestYour, draft: "kept" });
          }
        } catch { /* The durable received fact remains true through an unavailable Check. */ }
        return keep(waiting());
      } } });
  return renderTask({ editor: <p>Received <time dateTime={received.received_at}>{when(received.received_at, timezone)}</time>. {a3Copy.receivedWorkKept}</p>,
    read: { status: "ready", value: null, readAt: received.received_at },
    draft: false, pending: false, result: result ?? waiting(),
    commit: { verb: a3Copy.checkHandIn, cap: a3Copy.receivedVerificationPending,
      effects: () => [], blockers: () => [{ kind: "pending" }], run: async () => result ?? waiting() } });
}
